import './homesites-phases.scss';
import Base from '../base/base';

interface HomesitesPhasesOptions {
  /** Selector for the section that holds the pills and the maps */
  selector?: string;
  /** Selector for the phase pills, inside the section */
  buttonSelector?: string;
  /** Selector for the stacked map images, inside the section */
  mapSelector?: string;
  debug?: boolean;
  [key: string]: any;
}

/**
 * Switches the East Woods phase maps from the pills above them.
 *
 * Webflow renders both lists from the Homesites Phases collection and pairs
 * them by slug: a pill carries `data-phase-slug`, its map carries
 * `data-phase-map-slug`. This component only moves the active state between
 * the pairs — it never reads the collection itself.
 *
 * The maps are all `position: absolute` in one box, so without this they
 * stack and the last one wins. The stylesheet hides the inactive ones, but
 * only once this component marks the section ready, so a page that never
 * runs its JavaScript still shows a map rather than an empty frame.
 */
export default class HomesitesPhases extends Base {
  /** Webflow owns this one — the gold pill state built in the Designer. */
  static readonly ACTIVE_BUTTON_CLASS = 'pgb--active';

  /** This component owns this one; homesites-phases.scss defines it. */
  static readonly ACTIVE_MAP_CLASS = 'hmi--active';

  /** Marks the section as JavaScript-driven, which arms the hiding rule. */
  static readonly READY_CLASS = 'hs--ready';

  private config: {
    selector: string;
    buttonSelector: string;
    mapSelector: string;
  };

  private section: HTMLElement | null = null;

  /** Slug -> the pill and the map that answer for it. Unpaired slugs stay out. */
  private phases: Map<string, { button: HTMLElement; map: HTMLElement }> = new Map();

  /** The paired slugs in document order, which is the order Webflow sorted them. */
  private order: string[] = [];

  private activeSlug: string | null = null;

  private boundOnClick: (event: Event) => void;

  constructor(options: HomesitesPhasesOptions = {}) {
    super(options.debug || false);

    this.config = {
      selector: options.selector || '.homesites-section',
      buttonSelector: options.buttonSelector || '.homesites__phase-group-btn[data-phase-slug]',
      mapSelector: options.mapSelector || '.homesites__map-item[data-phase-map-slug]'
    };

    this.boundOnClick = this.onClick.bind(this);

    this.init();
  }

  init(): void {
    this.section = document.querySelector(this.config.selector);
    if (!this.section) {
      this.log(`"${this.config.selector}" not found, phase switching skipped`);
      return;
    }

    const maps = new Map<string, HTMLElement>();
    this.section.querySelectorAll<HTMLElement>(this.config.mapSelector).forEach((map) => {
      const slug = map.dataset.phaseMapSlug;
      if (slug) maps.set(slug, map);
    });

    this.section.querySelectorAll<HTMLElement>(this.config.buttonSelector).forEach((button) => {
      const slug = button.dataset.phaseSlug;
      if (!slug) return;

      const map = maps.get(slug);
      if (!map) {
        // A pill whose map did not publish. Leaving it unbound is better than
        // a click that empties the frame.
        this.log(`no map for phase "${slug}", pill left inert`);
        return;
      }

      this.phases.set(slug, { button, map });
      this.order.push(slug);
      button.addEventListener('click', this.boundOnClick);
    });

    if (!this.phases.size) {
      this.log('no phase pairs found, phase switching skipped');
      return;
    }

    this.section.classList.add(HomesitesPhases.READY_CLASS);
    this.activate(this.initialSlug());

    this.log(`HomesitesPhases initialized with ${this.phases.size} phases`);
  }

  /**
   * The phase to open on load: whichever pill the export already marks active,
   * else the first pair. Webflow ships no active pill today, so this is the
   * first one — "All Phases" — until someone sets one in the Designer.
   */
  private initialSlug(): string {
    const preset = this.order.find((slug) =>
      this.phases.get(slug)?.button.classList.contains(HomesitesPhases.ACTIVE_BUTTON_CLASS)
    );

    return preset || this.order[0];
  }

  /** The pills are anchors on `#`, so the jump has to be cancelled. */
  private onClick(event: Event): void {
    event.preventDefault();

    const slug = (event.currentTarget as HTMLElement).dataset.phaseSlug;
    if (slug) this.activate(slug);
  }

  /** Moves both active classes onto one pair and off every other. */
  private activate(slug: string): void {
    if (slug === this.activeSlug || !this.phases.has(slug)) return;

    this.phases.forEach(({ button, map }, key) => {
      const isActive = key === slug;
      button.classList.toggle(HomesitesPhases.ACTIVE_BUTTON_CLASS, isActive);
      map.classList.toggle(HomesitesPhases.ACTIVE_MAP_CLASS, isActive);
    });

    this.activeSlug = slug;
    this.log(`phase "${slug}" shown`);
  }

  /** Cleanup method — detach the click listeners. */
  destroy(): void {
    this.phases.forEach(({ button }) => button.removeEventListener('click', this.boundOnClick));
    this.phases.clear();
    this.order = [];
    this.section?.classList.remove(HomesitesPhases.READY_CLASS);
    this.section = null;
    this.activeSlug = null;
  }
}
